/* eslint-disable @typescript-eslint/no-unused-vars */
import { createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, PaginationState, RowData, SortingState, useReactTable, VisibilityState} from '@tanstack/react-table';
import React, { useState, FC, useEffect  } from 'react';
import {  Row, Col, Pagination, Form, Spinner, Button } from 'react-bootstrap';
import { SimulationResultRow } from "../core/_models";
import { formatNumber, getNameByISO3 } from "../../../../libs/misc";
import { useAuth } from '../../../../modules/auth';

declare module '@tanstack/react-table' { 
  interface ColumnMeta<TData extends RowData, TValue> { 
    headerClass: string; 
    cellClass:string
  }
}

type ImpactTableProps = {
  rows?: SimulationResultRow[]|null
  params?: any
  simulateInputOutput: (dataTable)=>void
}

const ImpactTable:FC<ImpactTableProps> = ({rows, params, simulateInputOutput})=>{
  const { currentUser } = useAuth();
  const reporterISO3User = currentUser?.economy;
  const [isLoading, setIsLoading] = useState(true)
  const [simulationResult, setSimulationResult] = useState<SimulationResultRow[]>([])
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  })
  const [globalFilter, setGlobalFilter] = useState<any>([])
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})

  const impactSortFn = (rowA, rowB, columnId) => {
    const econDataA = rowA.original.affected_econ?.find(data => data.econ === reporterISO3User);
    const econDataB = rowB.original.affected_econ?.find(data => data.econ === reporterISO3User);

    const impactA = econDataA ? (econDataA.tc + econDataA.td) : null; // Use null for 'N/A'
    const impactB = econDataB ? (econDataB.tc + econDataB.td) : null; // Use null for 'N/A'


    // Find current sorting direction for the column
    const sortDir = sorting.find((sort) => sort.id === columnId)?.desc;

    // Custom sorting logic to place 'N/A' values at the bottom
    if (impactA === null && impactB === null) return 0; // Both are 'N/A'

    if (sortDir) {
      // Descending order: Place null at the bottom
      if (impactA === null) return -1; // impactA is 'N/A', move it down
      if (impactB === null) return 1; // impactB is 'N/A', move it down
    } else {
      // Ascending order: Place null at the bottom
      if (impactA === null) return 1; // impactA is 'N/A', move it down
      if (impactB === null) return -1; // impactB is 'N/A', move it down
    }
    return impactA - impactB; // Ascending order for valid values
  };

  const columnHelper = createColumnHelper<SimulationResultRow>()

  const columns = [
    columnHelper.accessor('hs_code',{
      header: 'HS Code',
      cell: info=>{
        const targetURL = `${process.env.REACT_APP_TINA_BASE_URL}/app/dashboard/${params.rep}-${params.par}/commodity/${info.getValue()}?ref=negotiation&year=${params.year}`
        return (
          <a href={targetURL} target='blank'>
            <span className="badge text-white bg-dark">
              {info.getValue()}
            </span>
          </a>
        )
      },
      meta:{
        headerClass:'ps-4 w-100px',
        cellClass:'w-100px text-center'
      }
    }),
    columnHelper.accessor('desc',{
      header: 'Commodity Description',
      cell: info=>(
        <small>
          {info.getValue()}
        </small>),
      meta:{
        headerClass:'w-200px',
        cellClass:'w-200px text-center'
      }
    }),
    columnHelper.accessor('trade_val',{
      header: 'Current trade value (US$)',
      cell: info=>formatNumber(info.getValue()),
      meta:{
        headerClass:'max-w-100px',
        cellClass:'text-end'
      }
    }),
    columnHelper.accessor('tariff_old',{
      header: 'Current Tariff',
      cell: info=>info.getValue()?`${info.getValue().toFixed(2)}%`:'N/A',
      meta:{
        headerClass:'max-w-100px',
        cellClass:'text-center'
      },
      sortUndefined:'last'
    }),
    columnHelper.accessor('tariff_new',{
      header: 'Simulated Tariff',
      cell: info=>`${info.getValue()}%`,
      meta:{
        headerClass:'max-w-100px',
        cellClass:'text-center'
      },
      sortUndefined:'last'
    }),
    columnHelper.accessor((row)=>row.trade_val + (row.tc + row.td),{
      header: 'Simulated trade value (US$)',
      cell: info=> formatNumber(info.row.original.trade_val + (info.row.original.tc + info.row.original.td)),
      meta:{
        headerClass:'max-w-100px',
        cellClass:'text-end'
      },
      sortUndefined:'last'
    }),
    columnHelper.accessor((row) => row.tc + row.td,{
      header: 'Change in trade value (US$)',
      cell: info=> {
        const value = info.row.original.tc + info.row.original.td
        if(value>0){
          return (<span className="text-success fw-bolder">+{formatNumber(value)}</span>)
        }
        return (<span className="text-danger fw-bolder">-{formatNumber(value)}</span>)
      },
      meta:{
        headerClass:'max-w-100px',
        cellClass:'text-end'
      },
      sortUndefined:'last'
    }),
    columnHelper.accessor(row=>(((row.tc + row.td)/row.trade_val)*100).toFixed(2),{
      header: 'Change in trade value (%)',
      cell: info=> `${(((info.row.original.tc + info.row.original.td)/info.row.original.trade_val)*100).toFixed(2)}%`,
      meta:{
        headerClass:'max-w-100px',
        cellClass:'text-center'
      },
      
    }),
    {
      accessorKey:'impact',
      header: `Impact on ${getNameByISO3(reporterISO3User)} (US$)`,
      cell: info=>{
        const econData = info.row.original.affected_econ?.find((data)=>data.econ===reporterISO3User)
        const impact = econData?formatNumber(econData.tc + econData.td):'N/A'
        return (
          impact!=='N/A'?((econData.tc + econData.td)<0?<span className="text-danger fw-bolder">{impact}</span>:<span className="text-success fw-boldder">{impact}</span>):impact
        )
      },
      sortingFn:impactSortFn, //Cannot use accessor method to get custom parameter
      meta:{
        headerClass:'max-w-125px',
        cellClass:'text-center'
      },
    }
  ]

  // Cleanup function to reset the state when the component is unmounted 
  useEffect(()=>{
    setSimulationResult([])
    setIsLoading(true)
    return () => {
      setSimulationResult([])
      setIsLoading(true)
    };
  },[])

  useEffect(()=>{
    setSimulationResult(rows!)
    setColumnVisibility({
      "impact":(params.rep===reporterISO3User || params.par===reporterISO3User)?false:true,
    })
    setIsLoading(false)
  },[rows])

  const dataTable = useReactTable({
    data:simulationResult || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      pagination,
      globalFilter,
      sorting,
      columnVisibility
    },
  })

  function paginationRange() {
    const currentPage = pagination.pageIndex + 1;
    const totalPages = dataTable.getPageCount();
    const maxPagesToShow = 3;
    const halfRange = Math.floor(maxPagesToShow / 2);

    // Ensure startPage and endPage are within valid range
    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);

    // Adjust range to maintain maxPagesToShow
    if (endPage - startPage + 1 < maxPagesToShow) {
      if (currentPage - startPage >= halfRange) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      } else {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      }
    }

    const range:any = [];

    // Add first page and ellipsis if necessary
    if (startPage > 1) {
      range.push(1);
      if (startPage > 2) {
        range.push('...');
      }
    }

    // Add pages in the calculated range
    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }

    // Add last page and ellipsis if necessary
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        range.push('...');
      }
      range.push(totalPages);
    }

    return range;
  }

  const handlePageChange = (page) => {
    const currentPage = pagination.pageIndex+1
    const totalPages = dataTable.getPageCount()
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      dataTable.setPageIndex(page-1)
    }
  };

  const handleDisplayPageChange = (e) =>{
    setPagination({
      ...pagination,
      pageIndex: 0,
      pageSize: +e.target.value
    })
  }

  const pages = paginationRange();
  const totalCount = dataTable.getRowCount()
  const firstIndexPerPages = ((dataTable.getState().pagination.pageIndex) * dataTable.getState().pagination.pageSize) + 1
  const lastIndexPerPages = ((dataTable.getState().pagination.pageIndex) * dataTable.getState().pagination.pageSize) + dataTable.getRowModel().rows.length
  const displayHeader = dataTable.getHeaderGroups().map(headerGroup=>(
    <tr className='fw-normal py-4 text-center align-middle' key={headerGroup.id}>
    {
      headerGroup.headers.map(header=>{
        return (
          <th key={header.id} className={header.column.columnDef.meta?.headerClass}>
            <div 
              className={header.column.getCanSort()
                  ? 'cursor-pointer select-none'
                  : ''
              }
              title={
                header.column.getCanSort()
                  ? header.column.getNextSortingOrder() === 'asc'
                    ? 'Sort ascending'
                    : header.column.getNextSortingOrder() === 'desc'
                      ? 'Sort descending'
                      : 'Reset'
                  : undefined
              }
              onClick={header.column.getToggleSortingHandler()}>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              <span className="ms-2"> 
                {{
                  asc: (<i className="fa-solid fa-sort-up"></i>),
                  desc: (<i className="fa-solid fa-sort-down"></i>),
                  false: (<i className="fa-solid fa-sort"></i>),
                }[header.column.getIsSorted() as string] ?? null}
              </span>
              </div>
          </th>
        )
      })
    }
    </tr>
  ))
  const displayBody = dataTable.getRowModel().rows.map(row=>(
    <tr key={row.id} className='align-middle'>
      {
        row.getVisibleCells().map(cell=>{
          return (
            <td key={cell.id} className={cell.column.columnDef.meta?.cellClass}>
              {flexRender(
                cell.column.columnDef.cell, 
                cell.getContext())
              }
            </td>
          )
        })
      }
    </tr>
  ))
  return (
    <>
    {!isLoading && (simulationResult?.length??0)>0 ? (
      <>
        <Row className='my-3'>
          <Col md={6} className='d-flex justify-content-start align-items-center'>
            <i className="fa-solid fa-circle text-success fs-9 me-2"></i>Showing {firstIndexPerPages} to {lastIndexPerPages} of {totalCount} simulations results
            <div>
              <Button variant="warning" className="ms-3 text-black fw-bold" size="sm" onClick={()=>simulateInputOutput(dataTable.getFilteredRowModel().rows)}>
                <i className="fa-solid fa-magnifying-glass-chart text-black"></i> Export to Input/Output Analysis
              </Button>
            </div>
          </Col>
          <Col md={6} className='d-flex justify-content-end'>
            <Form.Group as={Row} className="mb-6" controlId="">
              <Form.Label className="text-end text-primary me-0" column>
                <i className="fa-solid fa-magnifying-glass fa-rotate-90 text-primary fs-9 me-2"></i>Search :
              </Form.Label>
              <Col md={6} className="d-flex align-items-center">
                <Form.Control
                  type="text"
                  className="form-control-sm"
                  onChange={e => dataTable.setGlobalFilter(String(e.target.value))} />
                </Col>
            </Form.Group>
          </Col>
        </Row>
        <div className="table-responsive">
          <table className="gy-3 table table-striped table-bordered table-hover" id="impact_result_table">
            <thead className='table-dark'>
              {displayHeader}
            </thead>    
            <tbody className="gy-1 overflow-auto">
              {displayBody}
            </tbody>
          </table>
        </div>
        <Row className='my-3'>
            <Col md={6} className='d-flex justify-content-start align-items-center'>
              Display
              <Form.Select
                size="sm"
                className="w-80px mx-2"
                onChange={handleDisplayPageChange}
                value={pagination.pageSize}>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </Form.Select>
              simulation result
            </Col>
            <Col md={6} className='d-flex justify-content-end'>
              <Pagination>
                <Pagination.Prev
                  onClick={() => dataTable.previousPage()}
                  disabled={!dataTable.getCanPreviousPage()} />
                {/* Start Dynamic Page */}
                {pages.map((page, index) => {
                  if (page === '...') {
                    return (
                      <Pagination.Ellipsis key={index} />
                    );
                  }
                  return (
                    <Pagination.Item
                      key={index}
                      active={page === pagination.pageIndex + 1}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Pagination.Item>
                  );
                })}
                {/* End Dynamic Page */}
                <Pagination.Next
                  onClick={() => dataTable.nextPage()}
                  disabled={!dataTable.getCanNextPage()} />
              </Pagination>
            </Col>
        </Row>
      </>
    ):(
      <div className="d-flex align-items-center justify-content-center" style={{ height: '50vh' }}>
        <Spinner animation="border" role="status" variant="primary" className="me-3" />
        <div className="loading-text">
          Loading
          <span className="dot">.</span>
          <span className="dot">.</span>
          <span className="dot">.</span>
        </div>
      </div>
    )}
    </>
  )

}

export {ImpactTable}

